import { z } from 'zod';

/**
 * 发信方式。
 *
 * - `direct` 内网直投：探测到企业自己的邮件服务器允许本机匿名投递，不需要账号密码
 * - `smtp` 走服务商或自建 SMTP，要账号密码
 */
export const mailModeSchema = z.enum(['direct', 'smtp']);
export type MailMode = z.infer<typeof mailModeSchema>;

export const mailEncryptionSchema = z.enum(['ssl', 'starttls', 'none']);
export type MailEncryption = z.infer<typeof mailEncryptionSchema>;

/** 已配置时对外的描述，不含任何口令。 */
export const mailDeliverySchema = z.object({
  configured: z.boolean(),
  mode: mailModeSchema.nullable(),
  host: z.string().nullable(),
  port: z.number().nullable(),
  encryption: mailEncryptionSchema.nullable(),
  fromAddress: z.string().nullable(),
  fromName: z.string().nullable(),
});

export type MailDelivery = z.infer<typeof mailDeliverySchema>;

/** 探测企业自己的邮件服务器：查 MX，再试一次无认证投递。 */
export const mailProbeResultSchema = z.object({
  ok: z.boolean(),
  host: z.string().nullable(),
  port: z.number().nullable(),
  /** 探测过程的逐行输出，直接显示给运维看——失败时这是唯一有用的线索。 */
  log: z.array(z.string()),
});

export type MailProbeResult = z.infer<typeof mailProbeResultSchema>;

export const saveDirectMailSchema = z.object({
  mode: z.literal('direct'),
  host: z.string().trim().min(1),
  port: z.number().int().positive(),
  fromAddress: z.string().trim().toLowerCase().email('请填写有效的发件地址'),
  fromName: z.string().trim().max(60).optional(),
});

export const saveSmtpMailSchema = z.object({
  mode: z.literal('smtp'),
  host: z.string().trim().min(1, '请填写 SMTP 主机'),
  port: z.number().int().positive(),
  encryption: mailEncryptionSchema,
  fromAddress: z.string().trim().toLowerCase().email('请填写有效的发件地址'),
  fromName: z.string().trim().max(60).optional(),
  username: z.string().trim().min(1, '请填写账号'),
  password: z.string().min(1, '请填写密码或授权码'),
});

export const saveMailDeliverySchema = z.discriminatedUnion('mode', [
  saveDirectMailSchema,
  saveSmtpMailSchema,
]);

export type SaveMailDelivery = z.infer<typeof saveMailDeliverySchema>;
